"use client";

const LOCKED_WALLET_PATTERNS = [
  "wallet is locked",
  "wallet locked",
  "unlock your wallet",
  "must unlock",
  "must be unlocked",
  "unlock the wallet",
  "authentication needed",
  "authentication required",
];

const USER_REJECTED_PATTERNS = [
  "user rejected",
  "user denied",
  "request rejected",
  "rejected request",
  "rejected the request",
  "user disapproved",
  "transaction cancelled",
  "user canceled",
  "user cancelled",
];

const getErrorCode = (error: unknown): number | string | null => {
  if (error && typeof error === "object" && "code" in error) {
    const code = (error as { code?: unknown }).code;
    if (typeof code === "number" || typeof code === "string") {
      return code;
    }
  }

  if (error instanceof Error && "cause" in error) {
    return getErrorCode(error.cause);
  }

  return null;
};

const getErrorText = (error: unknown): string => {
  if (error instanceof Error) {
    const causeText = "cause" in error && error.cause instanceof Error ? ` ${getErrorText(error.cause)}` : "";
    return `${error.name} ${error.message}${causeText}`.toLowerCase();
  }

  if (typeof error === "string") {
    return error.toLowerCase();
  }

  return "";
};

export const isWalletLockedError = (error: unknown): boolean => {
  const errorText = getErrorText(error);

  return LOCKED_WALLET_PATTERNS.some((pattern) => errorText.includes(pattern));
};

export const isWalletUserRejectedError = (error: unknown): boolean => {
  const errorText = getErrorText(error);
  const errorCode = getErrorCode(error);

  return (
    errorCode === 4001 ||
    USER_REJECTED_PATTERNS.some((pattern) => errorText.includes(pattern)) ||
    errorText.includes("userrejectedrequesterror")
  );
};

/** True when any level of the cause chain carries this numeric EIP-1193 / JSON-RPC code. */
const hasErrorCode = (error: unknown, code: number): boolean => {
  const seen = new Set<object>();
  let current: unknown = error;
  while (current && typeof current === "object" && !seen.has(current)) {
    seen.add(current);
    if ("code" in current && (current as { code?: unknown }).code === code) return true;
    current = "cause" in current ? (current as { cause?: unknown }).cause : undefined;
  }
  return false;
};

/**
 * EIP-1193 code 4001 is the only signal that the user actually declined.
 * Text-pattern matches in isWalletUserRejectedError also catch connector errors raised
 * after the wallet already broadcast, so those are reported rather than trusted.
 * Wrappers such as WalletActionError carry their own string code, hence the chain walk.
 */
export const isConfirmedUserRejection = (error: unknown): boolean => hasErrorCode(error, 4001);

// Reporting helper: must not throw on BigInt or circular provider payloads.
const safeStringify = (value: object): string => {
  try {
    return JSON.stringify(value, (_, v: unknown) => (typeof v === "bigint" ? v.toString() : v));
  } catch {
    return Object.prototype.toString.call(value);
  }
};

/** Innermost cause, serialized for error reporting: the raw wallet/provider error behind our mapped message. */
export const getRootCauseSummary = (
  error: unknown,
): { name: string; message: string; code: number | string | null } => {
  const seen = new Set<object>();
  let current: unknown = error;
  while (current && typeof current === "object" && "cause" in current && !seen.has(current)) {
    seen.add(current);
    const { cause } = current as { cause?: unknown };
    if (cause == null) break;
    current = cause;
  }
  if (current instanceof Error) {
    return { name: current.name, message: current.message, code: getErrorCode(current) };
  }
  // Providers can reject with a plain { code, message } object.
  if (current && typeof current === "object") {
    const { name, message } = current as { name?: unknown; message?: unknown };
    return {
      name: typeof name === "string" ? name : "object",
      message: typeof message === "string" ? message : safeStringify(current),
      code: getErrorCode(current),
    };
  }
  return { name: typeof current, message: String(current), code: null };
};

export const getWalletLockedMessage = (walletLabel = "wallet"): string => {
  return `Your ${walletLabel} is locked. Unlock it and try again.`;
};
